
import {
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import type { VgApiService } from '@videogular/ngx-videogular/core';
import type { VideoPlayerComponent } from '../sample-details/video-player/video-player.component';

@Component({
    standalone: true,
    selector: 'app-lazy-video-player',
    template: `<ng-container #container></ng-container>`,
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: { style: 'display: block; position: relative; width: 100%; height: 100%; overflow: hidden;' }
})
export class LazyVideoPlayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() videoResource: { url: string } | null = null;

  @Output() onPlayerReadyEmit = new EventEmitter<VgApiService>();

  @ViewChild('container', { read: ViewContainerRef, static: true })
  container!: ViewContainerRef;

  private componentRef: ComponentRef<VideoPlayerComponent> | null = null;
  private playerReadySub: Subscription | null = null;
  private destroyed = false;

  ngOnInit(): void {
    Promise.all([
      import(/* webpackChunkName: "videogular" */ '@videogular/ngx-videogular/core'),
      import(/* webpackChunkName: "videogular" */ '../sample-details/video-player/video-player.component'),
    ]).then(([_core, { VideoPlayerComponent }]) => {
      if (this.destroyed) {
        return;
      }
      this.container.clear();
      this.componentRef = this.container.createComponent(VideoPlayerComponent);
      this.componentRef.setInput('videoResource', this.videoResource);
      this.playerReadySub = this.componentRef.instance.onPlayerReadyEmit.subscribe((api: VgApiService) => {
        this.onPlayerReadyEmit.emit(api);
      });
      this.componentRef.changeDetectorRef.detectChanges();
    }).catch(err => {
      if (!environment.production) {
        console.error('Failed to load video player:', err);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['videoResource'] && this.componentRef) {
      this.componentRef.setInput('videoResource', this.videoResource);
      this.componentRef.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.playerReadySub?.unsubscribe();
    this.componentRef?.destroy();
  }
}
